import { useMemo } from 'react';

import {
  canShowOptionLetter,
  getOptionsPerColumn,
  parseMultipleChoiceTypes,
  sortOptions,
} from './multiple-choice.utils';
import { MultipleChoiceItems } from './multiple-choice.style';
import {
  MultipleChoiceButtonText,
  MultipleChoiceContent,
  MultipleChoiceTextContent,
  MultipleChoiceViewImage,
} from './multiple-choice-view.style';
import { MultipleChoiceSelectedAnswerTile } from './multiple-choice-selected-answer.style';
import {
  MultipleChoiceLetter,
  MultipleChoiceLetterImage,
  MultipleChoiceStatus,
} from './multiple-choice';
import MultpleChoiceTextImageView from './text-image/multiple-choice-text-image-view';
import MultipleChoiceImage from './image/multiple-choice-image';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ViewProps } from './multiple-choice.constants';
import MultipleChoiceSelectedAnswersList from './multiple-choice-selected-answers-list';
import { getOptionStatus } from './logic/get-option-status';

type Props = ViewProps & {
  isResponsive: boolean;
  selectedAnswers?: MultipleChoiceAnswerFragment;
  showAnswer: boolean;
};

const MultipleChoiceSelectedAnswers = (props: Props) => {
  const { properties, selectedAnswers, isResponsive, showAnswer } = props;
  const { multipleChoice } = properties;

  const { isList, isText, isImage, isTextAndImage, isFree } =
    parseMultipleChoiceTypes(properties);

  const canShowCorrectOrIncorrect = !isFree && showAnswer === true;

  const chosenAnswers = useMemo(
    () => selectedAnswers?.multipleChoiceItems || [],
    [selectedAnswers]
  );

  const hasAnswered = useMemo(() => {
    return chosenAnswers.length > 0;
  }, [chosenAnswers]);

  const sortedOptions = useMemo(
    () => sortOptions(multipleChoice.options),
    [multipleChoice.options]
  );

  const isOneOptionCorrect = useMemo(
    () =>
      multipleChoice.options.filter(o => o.correctAnswerBoolean).length === 1,
    [multipleChoice]
  );

  return (
    <>
      {isList && isText ? (
        <MultipleChoiceSelectedAnswersList
          key={selectedAnswers?.multipleChoiceItems?.length}
          {...props}
          selectedAnswers={selectedAnswers}
          showAnswer={canShowCorrectOrIncorrect}
        />
      ) : (
        <MultipleChoiceItems
          role={isOneOptionCorrect ? 'radiogroup' : 'group'}
          $display={properties.layout?.display}
          $isResponsive={isResponsive}
          data-selector="multi-choice-options-group"
        >
          {sortedOptions.map((option, index) => {
            const isOptionSelected = !!chosenAnswers.find(
              ca => ca.itemId === option.itemId && ca.isSelected
            );

            const { isCorrect, isIncorrect, isSelected } = getOptionStatus({
              showAnswer: canShowCorrectOrIncorrect,
              isOptionSelected,
              option,
              hasAnswered,
            });

            return (
              <MultipleChoiceSelectedAnswerTile
                data-selector="multi-choice-option-pill"
                key={index}
                $isText={isText}
                $itemsPerRow={getOptionsPerColumn(properties.multipleChoice)}
                $isActive={isOptionSelected}
                $isCorrect={isCorrect}
                $isIncorrect={isIncorrect}
                $isSelected={isSelected}
                $isDisabled={hasAnswered && !isSelected}
                $minHeight={multipleChoice.optionMinHeight || 80}
              >
                {canShowCorrectOrIncorrect && (
                  <MultipleChoiceStatus
                    isCorrect={hasAnswered ? isCorrect : undefined}
                    isIncorrect={hasAnswered ? isIncorrect : undefined}
                  />
                )}

                {isTextAndImage ? (
                  <MultpleChoiceTextImageView
                    optionMinHeight={multipleChoice.optionMinHeight}
                    imagePosition={multipleChoice.imagePosition}
                    showOptionLetter={multipleChoice.showOptionLetter}
                    option={option}
                    index={index}
                  />
                ) : isImage ? (
                  <MultipleChoiceViewImage>
                    <MultipleChoiceImage option={option} />
                    {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                      <MultipleChoiceLetterImage index={index} />
                    )}
                  </MultipleChoiceViewImage>
                ) : (
                  <MultipleChoiceContent>
                    <MultipleChoiceTextContent>
                      {canShowOptionLetter(multipleChoice.showOptionLetter) && (
                        <MultipleChoiceLetter index={index} />
                      )}
                      <MultipleChoiceButtonText>
                        {option.text}
                      </MultipleChoiceButtonText>
                    </MultipleChoiceTextContent>
                  </MultipleChoiceContent>
                )}
              </MultipleChoiceSelectedAnswerTile>
            );
          })}
        </MultipleChoiceItems>
      )}
    </>
  );
};

export default MultipleChoiceSelectedAnswers;
